import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Form, Input, Modal, Row, Skeleton, Space, Switch, Upload } from "antd";
import { LockOutlined, PlusOutlined, SaveOutlined, ShopOutlined } from "@ant-design/icons";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import StoreWarehouseSelector from "./components/StoreWarehouseSelector";
import { useConfigStore, useStoreConfigurationScope, useStoreWarehouseChoices } from "./useStoreConfigurationResources";
import { buildConfigStoreBody, ConfigStore, configStoreFingerprint, configStoreValues, storeRequestError, storeSlug } from "./storeConfigurationModel";

export default function StoreConfigurationEditor({ store, onClose, onSaved }: {
  store: ConfigStore | null; onClose: () => void; onSaved: (created: boolean) => void;
}) {
  const creating = !store;
  const { scope, headers, permissions, invalidate } = useStoreConfigurationScope();
  const detail = useConfigStore(store?.id || null);
  const warehouses = useStoreWarehouseChoices();
  const [form] = Form.useForm();
  const [dialog, dialogContext] = Modal.useModal();
  const [baseline, setBaseline] = useState<ConfigStore | null>(null);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const autoSlug = useRef(creating);
  const initialized = useRef(false);
  const mounted = useRef(true);
  const mutation = useRef<AbortController | null>(null);
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState("");
  const values = Form.useWatch([], { form, preserve: true }) || {};
  const allowed = creating ? permissions.canCreateStores() : permissions.canEditStores();
  const ownerId = store?.warehouse_id || Number(scope.warehouseId);
  const ownerName = baseline?.warehouse_name || warehouses.data?.find((warehouse) => warehouse.id === ownerId)?.name || store?.warehouse_name || "Établissement sélectionné";
  const loaded = creating || Boolean(baseline);
  const apply = (data: ConfigStore) => {
    form.setFieldsValue(configStoreValues(data)); setBaseline(data); setDirty(false); dirtyRef.current = false;
    setConflict(false); setError("");
  };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; mutation.current?.abort(); };
  }, []);
  useEffect(() => {
    if (creating && !initialized.current) {
      initialized.current = true;
      form.setFieldsValue({ store_name: "", store_slug: "", store_description: "", domain: "", logo: [],
        warehouse_ids: [ownerId], is_active: true });
    }
  }, [creating, form, ownerId]);
  useEffect(() => {
    if (!detail.data || savingRef.current) return;
    if (!dirtyRef.current) {
      form.setFieldsValue(configStoreValues(detail.data)); setBaseline(detail.data); setConflict(false);
    } else if (baseline && configStoreFingerprint(detail.data) !== configStoreFingerprint(baseline)) setConflict(true);
  }, [detail.data, baseline, form]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const discard = (action: () => void) => {
    if (savingRef.current) return;
    if (!dirtyRef.current) { action(); return; }
    dialog.confirm({ title: "Abandonner les modifications non enregistrées ?",
      content: "Les champs et le logo ajoutés pendant cette saisie seront abandonnés.",
      okText: "Abandonner les modifications", cancelText: "Continuer la saisie", onOk: action });
  };
  const reload = () => discard(() => { void detail.refresh().then((data) => { if (mounted.current) apply(data); })
    .catch((cause) => { if (mounted.current) setError(storeRequestError(cause, "Impossible de recharger la boutique. Votre saisie est conservée.")); }); });
  const blocked = !allowed || !loaded || saving || conflict || Boolean(detail.error) || Boolean(warehouses.error) || !warehouses.data;
  const reason = !allowed ? "La modification de cette boutique n'est pas autorisée."
    : conflict ? "Rechargez la boutique modifiée depuis une autre session."
    : detail.error || warehouses.error ? "Actualisez les données avant d'enregistrer."
    : !loaded || !warehouses.data ? "Chargement des informations de la boutique."
    : saving ? "Enregistrement en cours." : dirty ? "Vos modifications ne sont pas encore enregistrées."
    : creating ? "Renseignez l'identité et les établissements participants." : "Aucune modification en attente.";
  const submit = async () => {
    if (blocked || savingRef.current) return;
    savingRef.current = true; setSaving(true); setError("");
    const controller = new AbortController(); mutation.current = controller;
    try {
      const submitted = form.getFieldsValue(true);
      const [currentWarehouses, currentStore] = await Promise.all([warehouses.refresh(), creating ? Promise.resolve(null) : detail.refresh()]);
      if (!mounted.current || controller.signal.aborted) return;
      if (currentStore && baseline && configStoreFingerprint(currentStore) !== configStoreFingerprint(baseline)) { setConflict(true); return; }
      const available = new Set(currentWarehouses.map((warehouse) => warehouse.id));
      if (![ownerId, ...(submitted.warehouse_ids || []).map(Number)].every((id) => available.has(id))) {
        setError("Un établissement sélectionné n'est plus accessible. Votre sélection est conservée ; vérifiez les participants."); return;
      }
      const body = buildConfigStoreBody(submitted, ownerId, creating, permissions.canDeleteStores());
      if (creating) await apiClient.post(API_CONFIG.apiUrl("/ecommerce/stores"), body, { headers, signal: controller.signal });
      else await apiClient.put(API_CONFIG.apiUrl(`/ecommerce/stores/${store!.id}`), body, { headers, signal: controller.signal });
      if (!mounted.current || controller.signal.aborted) return;
      dirtyRef.current = false; setDirty(false);
      void invalidate(); onSaved(creating);
    } catch (cause) {
      if (mounted.current && !controller.signal.aborted) setError(storeRequestError(cause, "Enregistrement impossible. Votre saisie est conservée."));
    } finally { if (mounted.current) { savingRef.current = false; setSaving(false); } }
  };
  return <Modal open className="store-config-modal" title={creating ? "Nouvelle boutique" : "Modifier la boutique"}
    width={800} onCancel={() => discard(onClose)} maskClosable={!saving} closable={!saving} keyboard={!saving}
    footer={<div className="store-config-editor-footer"><small role="status">{reason}</small><Space>
      <Button disabled={saving} onClick={() => discard(onClose)}>Annuler</Button>
      <Button data-testid="store-config-save" type="primary" icon={<SaveOutlined />} loading={saving}
        disabled={blocked || (!creating && !dirty)} onClick={() => form.submit()}>{creating ? "Créer la boutique" : "Enregistrer les modifications"}</Button>
    </Space></div>}>
    {dialogContext}
    <div className="store-config-editor-scroll">
      {detail.error && <Alert type="error" showIcon message="La boutique n'a pas pu être actualisée. Votre saisie est conservée."
        action={<Button onClick={() => void detail.refresh().catch(() => {})}>Réessayer</Button>} />}
      {conflict && <Alert type="warning" showIcon message="Cette boutique a été modifiée depuis une autre session."
        description="Votre saisie est conservée. Rechargez les valeurs enregistrées avant de poursuivre."
        action={<Button onClick={reload}>Recharger les valeurs</Button>} />}
      {error && <Alert type="error" showIcon message={error} />}
      {!loaded && !detail.error && <Skeleton active paragraph={{ rows: 4 }} />}
      <Form form={form} layout="vertical" name="store-configuration" aria-label="Identité et accès de la boutique"
        disabled={!allowed || !loaded || saving} onFinish={() => void submit()}
        onValuesChange={(changed) => {
          setDirty(true); dirtyRef.current = true;
          if (changed.store_slug !== undefined) autoSlug.current = false;
          if (changed.store_name !== undefined && autoSlug.current) form.setFieldsValue({ store_slug: storeSlug(changed.store_name) });
        }}>
        <h3>Identité de la boutique</h3>
        <Row gutter={20}><Col xs={24} md={16}><Form.Item label="Nom de la boutique" name="store_name" rules={[{ required: true, whitespace: true, message: "Le nom de la boutique est requis." }]}>
          <Input placeholder="Nom de la boutique" autoComplete="off" /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item label="Logo de la boutique" name="logo" valuePropName="fileList"
            getValueFromEvent={(event) => Array.isArray(event) ? event : event?.fileList}>
            <Upload maxCount={1} listType="picture" accept="image/jpeg,image/png,image/webp" aria-label="Fichier du logo de la boutique"
              showUploadList={{ showRemoveIcon: Boolean(values.logo?.[0]?.originFileObj) }} beforeUpload={(file) => {
                if (["image/jpeg", "image/png", "image/webp"].includes(file.type)) return false;
                setError("Choisissez un logo JPG, PNG ou WebP."); return Upload.LIST_IGNORE;
              }}><Button icon={<PlusOutlined />}>Changer le logo</Button></Upload>
          </Form.Item></Col></Row>
        <Form.Item label="Description" name="store_description"><Input.TextArea rows={3} placeholder="Présentez votre boutique à vos clients." /></Form.Item>
        <h3>Adresse publique</h3><Row gutter={20}><Col xs={24} md={12}>
          <Form.Item label="Identifiant dans l'URL" name="store_slug" extra={creating ? "Minuscules, chiffres et tirets." : "L'identifiant public est fixé à la création de la boutique."}
            rules={[{ required: true, message: "L'identifiant est requis." }, { pattern: /^[a-z0-9-]+$/, message: "Utilisez des minuscules, chiffres et tirets." }]}>
            <Input readOnly={!creating} prefix={!creating ? <LockOutlined /> : undefined} />
          </Form.Item></Col><Col xs={24} md={12}><Form.Item label="Domaine personnalisé (facultatif)" name="domain" extra="Sans http:// ni https://. Laissez vide pour conserver l'adresse par défaut."
            rules={[{ pattern: /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/, message: "Saisissez un domaine valide, par exemple monsite.com." }]}>
            <Input placeholder="monsite.com" /></Form.Item></Col></Row>
        <h3>Établissements</h3><Form.Item label="Rattachement commercial"><Input aria-label="Rattachement commercial" value={ownerName} readOnly prefix={<ShopOutlined />} /></Form.Item>
        <p className="store-config-help">Le rattachement pilote les commandes et les encaissements et ne peut pas être changé ici.</p>
        <Form.Item label="Établissements participants" name="warehouse_ids" rules={[{ required: true, type: "array", min: 1, message: "Sélectionnez au moins un établissement participant." }]}>
          <StoreWarehouseSelector primaryWarehouseId={ownerId} disabled={saving || !loaded} /></Form.Item>
        <h3>Disponibilité</h3><Form.Item label="Boutique accessible au public" name="is_active" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={!creating && !permissions.canDeleteStores()} /></Form.Item>
        {!creating && !permissions.canDeleteStores() && <p className="store-config-help">Le changement de statut nécessite une autorisation supplémentaire.</p>}
      </Form>
    </div>
  </Modal>;
}
